import { useState, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '@/shared/lib/i18n/use-language'
import { useUpdateUserPreferences } from '@/shared/hooks/use-update-user-preferences'
import { useUser } from '@/shared/hooks/use-user'
import { LANGUAGE, SERVER_LANGUAGE_MAP } from '@/shared/constants/language'
import type { SelectOption } from '@/components/molecules/select-field'
import type {
  UserGender,
  UserContactChannel,
  UserLanguage,
} from '@/shared/types/global.types'

export type { UserGender, UserContactChannel }

const LANGUAGE_TO_SERVER: Record<string, UserLanguage> = {
  [LANGUAGE.EN]: 'English',
  [LANGUAGE.ES]: 'Spanish',
}

interface UseRentersStepTwoPageOptions {
  onContinue: () => void
  onBack: () => void
}

export function useRentersStepTwoPage({
  onContinue,
  onBack,
}: UseRentersStepTwoPageOptions) {
  const { t } = useTranslation()
  const { user, isLoading } = useUser()
  const { currentLanguage, updateLanguage, setLanguage } = useLanguage()
  const { mutate: updatePreferences } = useUpdateUserPreferences()

  const genderOptions = useMemo<SelectOption[]>(
    () => [
      {
        value: 'Woman',
        label: t('onboarding.renters.stepTwo.identityOptions.female'),
      },
      {
        value: 'Man',
        label: t('onboarding.renters.stepTwo.identityOptions.male'),
      },
      {
        value: 'NonBinary',
        label: t('onboarding.renters.stepTwo.identityOptions.nonBinary'),
      },
      {
        value: 'PreferNotToSay',
        label: t('onboarding.renters.stepTwo.identityOptions.preferNotToSay'),
      },
    ],
    [t],
  )

  const [gender, setGender] = useState<UserGender | ''>('')
  const [channels, setChannels] = useState<Set<UserContactChannel>>(new Set())

  useEffect(() => {
    if (!user) return

    if (user.gender) setGender(user.gender)
    if (user.contactChannels?.length) setChannels(new Set(user.contactChannels))
    if (user.language) {
      const lang = SERVER_LANGUAGE_MAP[user.language]
      if (lang) setLanguage(lang)
    }
  }, [user])

  const toggleChannel = useCallback((channel: UserContactChannel) => {
    setChannels((prev) => {
      const next = new Set(prev)
      if (next.has(channel)) next.delete(channel)
      else next.add(channel)
      return next
    })
  }, [])

  const handleContinue = useCallback(() => {
    const contactChannels = Array.from(channels)
    const language = LANGUAGE_TO_SERVER[currentLanguage]
    const preferences = {
      ...(language && { language }),
      ...(gender && { gender }),
      ...(contactChannels.length > 0 && { contactChannels }),
    }

    if (Object.keys(preferences).length > 0) {
      updatePreferences(preferences)
    }

    onContinue()
  }, [currentLanguage, gender, channels, updatePreferences, onContinue])

  const isFormValid = gender !== '' && channels.size > 0

  return {
    currentLanguage,
    updateLanguage,
    gender,
    setGender,
    genderOptions,
    channels,
    toggleChannel,
    handleContinue,
    handleBack: onBack,
    isLoading,
    isFormValid,
  }
}
